import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, detectLanguage, setLanguage as saveLanguage } from './translations';
import { getDefaultLanguage, getLanguageMeta, getRegionLanguages, isLanguageAllowed } from './languages';
import { regionConfig, getLocalizedRegionText } from '../config/regions';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLang] = useState(() => detectLanguage());

  const setLanguage = (lang) => {
    const next = isLanguageAllowed(lang) ? lang : getDefaultLanguage();
    setLang(next);
    saveLanguage(next);
  };

  useEffect(() => {
    const meta = getLanguageMeta(language);
    document.documentElement.lang = language;
    document.documentElement.dir = meta.dir || 'ltr';

    const tagline =
      getLocalizedRegionText(regionConfig.marketing_tagline, language) ||
      "AI second opinion";
    document.title = `${regionConfig.brand_name} — ${tagline}`;

    const descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        "content",
        getLocalizedRegionText(regionConfig.meta_description, language) ||
          `${regionConfig.brand_name} — ${tagline}`
      );
    }
  }, [language]);

  const t = (key) => {
    if (translations[language]?.[key]) return translations[language][key];
    for (const fb of getRegionLanguages()) {
      if (fb !== language && translations[fb]?.[key]) return translations[fb][key];
    }
    if (translations.en?.[key]) return translations.en[key];
    if (getRegionLanguages().includes('ru') && translations.ru?.[key]) return translations.ru[key];
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
